import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useSession } from '../lib/session';
import { fetchRecentFeedback, type FeedbackRow } from '../lib/admin-queries';
import {
  fetchSaleroomCharges,
  stageCounts,
  type SaleroomChargeRow,
} from '../lib/saleroom-admin-queries';
import { Heading, Text } from '../components/ui';
import { PeopleSection } from '../components/admin/PeopleSection';
import { ContentSection } from '../components/admin/ContentSection';
import { SaleroomSection } from '../components/admin/SaleroomSection';
import { NotificationsSection } from '../components/admin/notifications/NotificationsSection';
import { CommsSection } from '../components/admin/CommsSection';
import { SettingsSection } from '../components/admin/SettingsSection';
import { AdminOrders } from '../components/admin/AdminOrders';
import { AdminDashboard } from '../components/admin/AdminDashboard';
import './Admin.css';

// ---------------------------------------------------------------------------
// Admin console. A single flat set of views in a grouped nav (sidebar on
// desktop, drawer + bottom bar on mobile) - no more tabs-within-tabs. Each view
// renders one existing panel (re-homed unchanged); Dashboard + Orders are new.
// The shell owns nav + the two data feeds that drive nav badges (Contact inbox
// unread + Saleroom orders), and the Members -> Campaign recipient handoff.
// ---------------------------------------------------------------------------

export type AdminView =
  | 'dashboard' | 'orders' | 'auctions' | 'posts' | 'members' | 'inbox'
  | 'campaigns' | 'emaillog'
  | 'shipping' | 'pricing' | 'appearance' | 'drawers' | 'welcome' | 'templates' | 'notifications'
  | 'staff' | 'impersonate';

const VIEW_LABEL: Record<AdminView, string> = {
  dashboard: 'Dashboard', orders: 'Orders', auctions: 'Live auctions', posts: 'Posts',
  members: 'Members', inbox: 'Inbox', campaigns: 'Email campaigns', emaillog: 'Email log',
  shipping: 'Shipping', pricing: 'Pricing', appearance: 'Appearance', drawers: 'Drawers',
  welcome: 'Welcome video', templates: 'Email templates', notifications: 'Notifications',
  staff: 'Staff & roles', impersonate: 'Impersonate',
};

const ALL_VIEWS = Object.keys(VIEW_LABEL) as AdminView[];

type Group = { label: string; items: AdminView[] };
const GROUPS: Group[] = [
  { label: 'Work', items: ['dashboard', 'orders', 'auctions', 'posts', 'members', 'inbox'] },
  { label: 'Broadcast', items: ['campaigns', 'emaillog'] },
  { label: 'Notifications', items: ['notifications'] },
  { label: 'Setup', items: ['shipping', 'pricing', 'appearance', 'drawers', 'welcome', 'templates'] },
  { label: 'Admin', items: ['staff', 'impersonate'] },
];

// The handful of destinations on the mobile bottom bar (plus a "More" drawer).
const BOTTOM_BAR: AdminView[] = ['dashboard', 'orders', 'posts', 'members'];

// Old hashes (bookmarks + the previous section/sub scheme) map onto views.
const LEGACY_HASH: Record<string, AdminView> = {
  'people/members': 'members', members: 'members', testers: 'members',
  'people/admins': 'staff', admins: 'staff',
  'people/impersonate': 'impersonate', dev: 'impersonate',
  'content/posts': 'posts', content: 'posts', banners: 'posts',
  'content/drawers': 'drawers',
  'content/welcome': 'welcome',
  'saleroom/bids': 'auctions', saleroom: 'auctions',
  'saleroom/charges': 'orders', charges: 'orders',
  'saleroom/shipping': 'shipping',
  'saleroom/notifications': 'notifications',
  'comms/compose': 'campaigns', comms: 'campaigns',
  'comms/inbox': 'inbox', feedback: 'inbox',
  'comms/log': 'emaillog', emaillog: 'emaillog',
  'comms/templates': 'templates',
  'settings/pricing': 'pricing', billing: 'pricing', settings: 'pricing',
  'settings/appearance': 'appearance', appearance: 'appearance',
};

function readHash(): AdminView {
  if (typeof window === 'undefined') return 'dashboard';
  const h = window.location.hash.replace(/^#/, '');
  if (!h) return 'dashboard';
  if ((ALL_VIEWS as string[]).includes(h)) return h as AdminView;
  if (LEGACY_HASH[h]) return LEGACY_HASH[h];
  return 'dashboard';
}

export default function Admin() {
  const { profile, loading } = useSession();

  const [view, setView] = useState<AdminView>(readHash);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Desktop: collapse the sidebar to reclaim width (persisted). Mobile uses
  // the drawer instead, so this only affects >=900px.
  const [railCollapsed, setRailCollapsed] = useState(() => {
    try { return localStorage.getItem('cc.adminRailCollapsed') === '1'; } catch { return false; }
  });
  const toggleRail = useCallback(() => {
    setRailCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem('cc.adminRailCollapsed', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  }, []);

  // Keep the hash in sync (deep links + back button).
  useEffect(() => {
    if (window.location.hash.replace(/^#/, '') !== view) {
      window.history.replaceState(null, '', `#${view}`);
    }
  }, [view]);
  useEffect(() => {
    function onHashChange() { setView(readHash()); }
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const go = useCallback((v: AdminView) => {
    setView(v);
    setDrawerOpen(false);
    if (typeof window !== 'undefined') window.scrollTo({ top: 0 });
  }, []);

  // Contact inbox - loaded here so the unread count can badge the nav.
  const [feedback, setFeedback] = useState<FeedbackRow[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Members -> Campaign recipient handoff.
  const [composeHandoff, setComposeHandoff] = useState<{ email: string; display_name: string | null }[]>([]);
  const [composeNonce, setComposeNonce] = useState(0);
  const onComposeTo = useCallback((recipients: { email: string; display_name: string | null }[]) => {
    setComposeHandoff(recipients);
    setComposeNonce((n) => n + 1);
    go('campaigns');
  }, [go]);

  // Saleroom orders - loaded here so the nav can badge outstanding work.
  const [saleroomRows, setSaleroomRows] = useState<SaleroomChargeRow[]>([]);
  const [saleroomLoading, setSaleroomLoading] = useState(false);
  const [saleroomLoaded, setSaleroomLoaded] = useState(false);
  const [saleroomError, setSaleroomError] = useState<string | null>(null);

  const refreshSaleroom = useCallback(async () => {
    setSaleroomLoading(true);
    setSaleroomError(null);
    try {
      setSaleroomRows(await fetchSaleroomCharges());
    } catch (err: any) {
      setSaleroomError(err?.message ?? 'Failed to load Saleroom orders.');
    } finally {
      setSaleroomLoading(false);
      setSaleroomLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!profile) return;
    if (profile.role !== 'admin' && profile.role !== 'developer') return;
    const ctrl = new AbortController();
    (async () => {
      try {
        const fb = await fetchRecentFeedback(50, ctrl.signal).catch((err) => {
          if (err?.name === 'AbortError') throw err;
          return [];
        });
        if (ctrl.signal.aborted) return;
        setFeedback(fb);
      } catch (err: any) {
        if (ctrl.signal.aborted || err?.name === 'AbortError') return;
        setError(err?.message ?? 'Failed to load admin data.');
      } finally {
        if (!ctrl.signal.aborted) setLoadingData(false);
      }
    })();
    return () => ctrl.abort();
  }, [profile?.id, profile?.role]);

  useEffect(() => {
    if (!profile) return;
    if (profile.role !== 'admin' && profile.role !== 'developer') return;
    void refreshSaleroom();
  }, [profile?.id, profile?.role, refreshSaleroom]);

  const isDeveloper = profile?.role === 'developer';
  const canImpersonate =
    isDeveloper || (profile?.role === 'admin' && profile?.permissions?.can_impersonate === true);

  const unread = useMemo(() => feedback.filter((f) => !f.is_read).length, [feedback]);
  const counts = useMemo(() => stageCounts(saleroomRows), [saleroomRows]);
  const ordersBadge = counts.needs_action + counts.ready_to_ship;

  // Per-view nav badge (null = none).
  const badgeFor = useCallback((v: AdminView): number | null => {
    if (v === 'orders') return ordersBadge || null;
    if (v === 'inbox') return unread || null;
    return null;
  }, [ordersBadge, unread]);

  // Hide role-gated views from the nav.
  const isVisible = useCallback((v: AdminView): boolean => {
    if (v === 'staff') return !!isDeveloper;
    if (v === 'impersonate') return !!canImpersonate;
    return true;
  }, [isDeveloper, canImpersonate]);

  if (loading) return null;
  if (!profile || (profile.role !== 'admin' && profile.role !== 'developer')) {
    return <Navigate to="/" replace />;
  }
  // A deep link to a now-hidden view falls back to the dashboard.
  const activeView = isVisible(view) ? view : 'dashboard';

  function renderView(v: AdminView) {
    switch (v) {
      case 'dashboard':
        return <AdminDashboard rows={saleroomRows} unread={unread} displayName={profile!.display_name ?? null} onGo={go} />;
      case 'orders':
        return (
          <AdminOrders
            rows={saleroomRows}
            loading={saleroomLoading}
            loaded={saleroomLoaded}
            error={saleroomError}
            onRefresh={refreshSaleroom}
          />
        );
      case 'auctions':
      case 'shipping': {
        const tab = v === 'auctions' ? 'bids' : 'shipping';
        return (
          <SaleroomSection
            tab={tab}
            charges={{ rows: saleroomRows, loading: saleroomLoading, loaded: saleroomLoaded, error: saleroomError, onRefresh: refreshSaleroom }}
          />
        );
      }
      case 'notifications':
        return <NotificationsSection />;
      case 'posts': return <ContentSection tab="posts" meId={profile!.id} />;
      case 'drawers': return <ContentSection tab="drawers" meId={profile!.id} />;
      case 'welcome': return <ContentSection tab="welcome" meId={profile!.id} />;
      case 'members':
      case 'staff':
      case 'impersonate': {
        const tab = v === 'members' ? 'members' : v === 'staff' ? 'admins' : 'impersonate';
        return (
          <PeopleSection
            tab={tab}
            me={{ id: profile!.id, role: profile!.role }}
            isDeveloper={!!isDeveloper}
            canImpersonate={!!canImpersonate}
            onComposeTo={onComposeTo}
          />
        );
      }
      case 'inbox':
      case 'campaigns':
      case 'emaillog':
      case 'templates': {
        const tab = v === 'inbox' ? 'inbox' : v === 'campaigns' ? 'compose' : v === 'emaillog' ? 'log' : 'templates';
        return (
          <CommsSection
            tab={tab}
            feedback={feedback}
            setFeedback={setFeedback}
            feedbackLoading={loadingData}
            composeHandoff={composeHandoff}
            composeNonce={composeNonce}
          />
        );
      }
      case 'pricing': return <SettingsSection tab="pricing" />;
      case 'appearance': return <SettingsSection tab="appearance" />;
    }
  }

  const navList = (
    <nav className="adminx__nav" aria-label="Admin sections">
      {GROUPS.map((g) => {
        const items = g.items.filter(isVisible);
        if (items.length === 0) return null;
        return (
          <div key={g.label} className="adminx__navgroup">
            <p className="adminx__navlabel">{g.label}</p>
            {items.map((v) => {
              const badge = badgeFor(v);
              return (
                <button
                  key={v}
                  type="button"
                  aria-current={activeView === v ? 'page' : undefined}
                  className={`adminx__navitem ${activeView === v ? 'adminx__navitem--active' : ''}`}
                  onClick={() => go(v)}
                >
                  <span>{VIEW_LABEL[v]}</span>
                  {badge ? <span className="adminx__navbadge">{badge}</span> : null}
                </button>
              );
            })}
          </div>
        );
      })}
    </nav>
  );

  return (
    <div className={`adminx${railCollapsed ? ' adminx--collapsed' : ''}`}>
      {/* Desktop sidebar */}
      <aside className="adminx__rail">
        <div className="adminx__brand">
          <span className="kicker">Admin</span>
        </div>
        {navList}
      </aside>

      {/* Mobile top bar */}
      <header className="adminx__topbar">
        <button type="button" className="adminx__menubtn" aria-label="Open menu" onClick={() => setDrawerOpen(true)}>
          <span aria-hidden="true">≡</span>
        </button>
        <span className="adminx__topbar-title">{VIEW_LABEL[activeView]}</span>
        <span className="adminx__topbar-spacer" aria-hidden="true" />
      </header>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="adminx__drawer">
          <div className="adminx__drawer-scrim" onClick={() => setDrawerOpen(false)} aria-hidden="true" />
          <div className="adminx__drawer-panel" role="dialog" aria-modal="true" aria-label="Admin menu">
            <div className="adminx__drawer-head">
              <span className="kicker">Admin</span>
              <button type="button" className="adminx__menubtn" aria-label="Close menu" onClick={() => setDrawerOpen(false)}>
                <span aria-hidden="true">×</span>
              </button>
            </div>
            {navList}
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="adminx__main admin">
        <button
          type="button"
          className="adminx__railtoggle"
          onClick={toggleRail}
          aria-label={railCollapsed ? 'Show menu' : 'Hide menu'}
          aria-expanded={!railCollapsed}
          title={railCollapsed ? 'Show menu' : 'Hide menu'}
        >
          <span aria-hidden="true">≡</span>
        </button>
        <div className="adminx__view">
          {error && (
            <div className="admin__error">
              <p className="kicker">Problem</p>
              <p>{error}</p>
            </div>
          )}
          {activeView !== 'dashboard' && (
            <header className="adminx__viewhead">
              <Heading level="h2">{VIEW_LABEL[activeView]}</Heading>
              <Text role="caption" muted>Signed in as {profile.role}</Text>
            </header>
          )}
          <section className="adminx__panel">
            {renderView(activeView)}
          </section>
        </div>
      </main>

      {/* Mobile bottom bar */}
      <nav className="adminx__bottombar" aria-label="Primary">
        {BOTTOM_BAR.map((v) => {
          const badge = badgeFor(v);
          return (
            <button
              key={v}
              type="button"
              aria-current={activeView === v ? 'page' : undefined}
              className={`adminx__bottomitem ${activeView === v ? 'adminx__bottomitem--active' : ''}`}
              onClick={() => go(v)}
            >
              <span className="adminx__bottomlabel">{VIEW_LABEL[v]}</span>
              {badge ? <span className="adminx__navbadge">{badge}</span> : null}
            </button>
          );
        })}
        <button type="button" className="adminx__bottomitem" onClick={() => setDrawerOpen(true)}>
          <span className="adminx__bottomlabel">More</span>
        </button>
      </nav>
    </div>
  );
}
